import { createQueryKeys } from '@lukemorales/query-key-factory';
import {
  ITemplateColumnInfo,
  normalizeTemplateColumnInfoList,
} from '../../../components/units/Management/AiAgent/Template/ColumnMapping/types';
import { aiAgentApis } from '../../apis/ai-agent/apis';
import { IAsCsTemplate, IGetAsCsTemplateListParams } from '../../apis/ai-agent/template/types';
import type {
  IEquipmentModel,
  IGetAiAgentModelListParams,
  IGetAiAgentModelSettingsHistoryParams,
  IModelSettingHistoryItem,
} from '../../apis/ai-agent/model/types';
import {
  IAiAgentCompany,
  IAiAgentServiceOperationSettings,
  IAiAgentServiceOperationSettingsHistory,
  IGetAiAgentCompanyListParams,
  IGetAiAgentServiceOperationSettingsHistoryParams,
} from '../../apis/ai-agent/company/types';
import { ApiResponse } from '../../types/response/types';
import {
  IAiAgentLearningUsage,
  ICheckFileNameParams,
  IGetManualHistoryParams,
  IGetManualsParams,
  ILinkedModelsParams,
  IManual,
  IManualHistory,
} from '../../apis/ai-agent/manual/types';
import type { IAiAgentAsCsListItem, IAsCsFilesParams, IGetAiAgentAsCsListParams } from '../../apis/ai-agent/ascs/type';
import { IAsCsProcessDetail, IAsCsReceiptDetail } from '../../../components/units/Management/AiAgent/AsCs/Details/types';

export const aiAgentQueries = createQueryKeys('aiAgent', {
  asCsTemplateList: (params: IGetAsCsTemplateListParams) => ({
    queryKey: [params.companyId, params.pg, params.pgSize, params.name, params.startDt, params.endDt],
    queryFn: async (): Promise<ApiResponse<IAsCsTemplate>> => {
      const response = await aiAgentApis.template.getAsCsTemplateList(params);
      return {
        count: response.data.data.totalCount,
        result: response.data.data.list,
      };
    },
  }),
  templateColumnInfo: () => ({
    queryKey: ['templateColumnInfo'],
    queryFn: async (): Promise<ApiResponse<ITemplateColumnInfo>> => {
      const response = await aiAgentApis.template.getTemplateColumnInfo();
      const normalizedList = normalizeTemplateColumnInfoList(response.data);
      return {
        result: normalizedList,
        count: normalizedList.length,
      };
    },
  }),
  equipmentModelList: (params: IGetAiAgentModelListParams) => ({
    queryKey: [params.companyId, params.searchType, params.keyword, params.useYn, params.page, params.size],
    queryFn: async (): Promise<ApiResponse<IEquipmentModel>> => {
      const response = await aiAgentApis.model.getAiAgentModelList(params);
      return {
        count: response.data.totalCount,
        result: response.data.responses,
      };
    },
  }),
  serviceOperationSettings: (companyId: number) => ({
    queryKey: [companyId],
    queryFn: async (): Promise<ApiResponse<IAiAgentServiceOperationSettings>> => {
      const response = await aiAgentApis.company.getAiAgentServiceOperationSettings(companyId);
      return {
        result: [response.data],
        count: 1,
      };
    },
  }),
  serviceOperationSettingsHistory: (params: IGetAiAgentServiceOperationSettingsHistoryParams) => ({
    queryKey: [params.companyId, params.page, params.size, params.startDate, params.endDate, params.managerKeyword],
    queryFn: async (): Promise<ApiResponse<IAiAgentServiceOperationSettingsHistory>> => {
      const response = await aiAgentApis.company.getAiAgentServiceOperationSettingsHistory(params);
      return {
        result: response.data.list,
        count: response.data.totalCount,
      };
    },
  }),
  aiAgentCompanies: (params: IGetAiAgentCompanyListParams) => ({
    queryKey: [params.size, params.page, params.companyName, params.companyId],
    queryFn: async (): Promise<ApiResponse<Partial<IAiAgentCompany>>> => {
      const response = await aiAgentApis.company.getAiAgentCompanyList(params);
      return {
        count: response.data.totalCount,
        result: response.data.responses,
      };
    },
  }),
  aiAgentManuals: (params: IGetManualsParams) => ({
    queryKey: [
      params.companyId,
      params.page,
      params.size,
      params.fileName,
      params.taskStatuses,
      params.regServiceChannels,
      params.modelId,
    ],
    queryFn: async (): Promise<ApiResponse<IManual>> => {
      const response = await aiAgentApis.manual.getManuals(params);
      return {
        count: response.data.totalCount,
        result: response.data.data,
      };
    },
  }),
  aiAgentManualHistory: (params: IGetManualHistoryParams) => ({
    queryKey: [
      params.companyId,
      params.fileUploadId,
      params.startDt,
      params.endDt,
      params.page,
      params.size,
      params.regId,
      params.regServiceChannels,
      params.historyTypes,
    ],
    queryFn: async (): Promise<ApiResponse<IManualHistory>> => {
      const response = await aiAgentApis.manual.getManualHistory(params);
      return {
        count: response.data.totalCount,
        result: response.data.data,
      };
    },
  }),
  aiAgentLearningUsage: (params: { companyId: number }) => ({
    queryKey: [params.companyId],
    queryFn: async (): Promise<ApiResponse<IAiAgentLearningUsage>> => {
      const response = await aiAgentApis.manual.getAiAgentLearningUsage(params);
      return {
        result: [response.data],
        count: 1,
      };
    },
  }),
  checkFileName: (params: ICheckFileNameParams) => ({
    queryKey: [params.companyId, params.fileName],
    queryFn: async (): Promise<'Y' | 'N'> => {
      const response = await aiAgentApis.manual.checkFileName(params);
      return response.data;
    },
  }),
  linkedModels: (params: ILinkedModelsParams) => ({
    queryKey: [params.companyId, params.fileUploadId],
    queryFn: async (): Promise<ApiResponse<IEquipmentModel>> => {
      const response = await aiAgentApis.manual.getLinkedModels(params);
      return {
        count: response.data.length,
        result: response.data,
      };
    },
  }),
  asCsList: (params: IGetAiAgentAsCsListParams) => ({
    queryKey: [params],
    queryFn: async (): Promise<ApiResponse<IAiAgentAsCsListItem>> => {
      const response = await aiAgentApis.ascs.getAsCsList(params);
      return {
        count: response.data.totalCount,
        result: response.data.data,
      };
    },
  }),
  asCsDetails: (params: { id: number }) => ({
    queryKey: [params.id],
    queryFn: async (): Promise<ApiResponse<IAsCsReceiptDetail>> => {
      const response = await aiAgentApis.ascs.getAsCsDetail(params);
      return {
        result: [response.data],
        count: 1,
      };
    },
  }),
  asCsProcessHistory: (params: { id: number; page: number; size: number }) => ({
    queryKey: [params.id, params.page, params.size],
    queryFn: async (): Promise<ApiResponse<IAsCsProcessDetail>> => {
      const response = await aiAgentApis.ascs.getAsCsProcessHistory(params);
      return {
        count: response.data.totalCount,
        result: response.data.data,
      };
    },
  }),
  asCsProcessHistoryDetails: (params: { id: number; processId: number }) => ({
    queryKey: [params.id, params.processId],
    queryFn: async (): Promise<ApiResponse<IAsCsProcessDetail>> => {
      const response = await aiAgentApis.ascs.getAsCsProcessHistoryDetail(params);
      return {
        result: [response.data],
        count: 1,
      };
    },
  }),
  asCsFiles: (params: IAsCsFilesParams) => ({
    queryKey: [
      params.companyId,
      params.page,
      params.size,
      params.modelId,
      params.taskStatuses,
      params.regServiceChannels,
      params.searchType,
      params.keyword,
    ],
    queryFn: async (): Promise<ApiResponse<any>> => {
      const response = await aiAgentApis.ascs.getAsCsFiles(params);
      return {
        result: response.data.data,
        count: response.data.totalCount,
      };
    },
  }),
  modelSettingHistory: (params: IGetAiAgentModelSettingsHistoryParams) => ({
    queryKey: [
      params.companyId,
      params.modelId,
      params.page,
      params.size,
      params.startDate,
      params.endDate,
      params.changeType,
      params.useYn,
      params.manager,
    ],
    queryFn: async (): Promise<ApiResponse<IModelSettingHistoryItem>> => {
      const response = await aiAgentApis.model.getAiAgentModelSettingsHistory(params);
      return {
        count: response.data.totalCount,
        result: response.data.list,
      };
    },
  }),
});
